import { gql } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { RiExternalLinkFill } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { formatIsoDateToOrgDate } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { ENROLLMENT_PERIOD_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { onImportCompleteCallback } from "../types";

const FormSchema = z.object({
  enrollmentPeriodId: z
    .string({ message: "This field is required" })
    .min(1, { message: "This field is required" }),
});
type FormType = z.infer<typeof FormSchema>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: onImportCompleteCallback;
  targetEnrollmentPeriodId: uuid;
};
export const ImportEnrollmentSchoolDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onImportComplete,
  targetEnrollmentPeriodId,
}) => {
  const importSchoolGrades =
    FSQ.useImportSchoolsAndGradesFromOthersForEnrollmentPeriod();
  const importConnections =
    FSQ.useImportBoundaryConnectionsFromEnrollmentPeriod();
  const toast = useAvelaToast();

  const organization = useOrganization();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetEnrollmentPeriodForSchoolGradesImport,
    GQL.GetEnrollmentPeriodForSchoolGradesImportVariables
  >(
    gql`
      query GetEnrollmentPeriodForSchoolGradesImport(
        $organizationId: uuid!
        $targetEnrollmentPeriodId: uuid!
      ) {
        enrollment_period(
          where: {
            id: { _neq: $targetEnrollmentPeriodId }
            organization_id: { _eq: $organizationId }
            deleted_at: { _is_null: true }
          }
        ) {
          id
          name
          key
          updated_at
        }
      }
    `,
    {
      variables: {
        organizationId: organization.toNullable()?.id ?? "",
        targetEnrollmentPeriodId,
      },
      skip: !organization.hasData(),
    }
  );

  const importEnrollmentSchools = React.useCallback(
    async (values: FormType) => {
      toast.closeAll();

      try {
        const { data: schoolGradesResult } =
          await importSchoolGrades.mutateAsync({
            path: {
              enrollmentPeriodId: targetEnrollmentPeriodId,
              sourceEnrollmentPeriodId: values.enrollmentPeriodId,
            },
          });

        const { data: connectionsResult } = await importConnections.mutateAsync(
          {
            path: {
              enrollmentPeriodId: targetEnrollmentPeriodId,
              sourceEnrollmentPeriodId: values.enrollmentPeriodId,
            },
          }
        );

        if (
          !schoolGradesResult ||
          !schoolGradesResult?.data ||
          !connectionsResult ||
          !connectionsResult?.data
        ) {
          toast.genericError();
          return;
        }

        if (
          schoolGradesResult.data.errors.length > 0 ||
          connectionsResult.data.errors.length > 0
        ) {
          toast.error({
            title: "Some or all schools were not imported",
            description: "Some or all schools were not imported due to errors",
          });
          return onImportComplete({
            data: schoolGradesResult.data.schools,
            errors: schoolGradesResult.data.errors,
            connectionData: connectionsResult.data.connections,
            connectionErrors: connectionsResult.data.errors,
          });
        }

        const ep = remoteData
          .toNullable()
          ?.enrollment_period.find((ep) => ep.id === values.enrollmentPeriodId);

        // Success
        toast({
          title: "Imported from existing",
          description:
            "Schools and grades imported successfully" +
            (ep ? ` from ${ep.name}` : ""),
          status: "info",
          isClosable: true,
        });

        return onImportComplete({
          data: schoolGradesResult.data.schools,
          connectionData: connectionsResult.data.connections,
        });
      } catch (error: any) {
        if (error.error?.message) {
          toast.error({
            title: "Something went wrong",
            description: error.error.message,
          });
        } else {
          toast.genericError();
        }
      } finally {
        onClose();
      }
    },
    [
      onClose,
      onImportComplete,
      remoteData,
      importSchoolGrades,
      importConnections,
      targetEnrollmentPeriodId,
      toast,
    ]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={{ enrollmentPeriodId: "" }}
        onSubmit={importEnrollmentSchools}
        validate={validateWithZod(FormSchema)}
      >
        {(formik) => (
          <ModalContent as={Form} noValidate>
            <ModalHeader>Import form</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="4">
                <Text>
                  Select an enrollment period to import items from it.
                </Text>
                <Alert status="warning" rounded="md">
                  <AlertIcon />
                  <AlertDescription>
                    The import will skip any existing content.
                  </AlertDescription>
                </Alert>
                <RemoteDataView
                  remoteData={remoteData}
                  error={(error) => <GenericError error={error} />}
                  loading={<Skeleton width="100%" height="2rem" />}
                >
                  {(data) => (
                    <FormikSearchSelect
                      name="enrollmentPeriodId"
                      label="Enrollment period to import from"
                      items={data.enrollment_period.map((ep) => ({
                        key: ep.id,
                        label: ep.name,
                        description: (
                          <>
                            <Text>{ep.key}</Text>
                            <Text>
                              Last modified on{" "}
                              {formatIsoDateToOrgDate(
                                ep.updated_at,
                                organization
                              )}
                            </Text>
                          </>
                        ),
                        action: (
                          <Tooltip label={`Open ${ep.name} in new tab`}>
                            <Link
                              aria-label={`Open ${ep.name} in new tab`}
                              target="_blank"
                              onClick={(event) => event.stopPropagation()}
                              href={organization
                                .map((org) =>
                                  Url.OrgAdmin.Enrollments.edit(
                                    org,
                                    ep.id,
                                    ENROLLMENT_PERIOD_TAB_HASH.SCHOOLS
                                  )
                                )
                                .withDefault("#")}
                            >
                              <Icon as={RiExternalLinkFill} />
                            </Link>
                          </Tooltip>
                        ),
                      }))}
                    />
                  )}
                </RemoteDataView>
              </Flex>
            </ModalBody>
            <ModalFooter gap="3">
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={formik.isSubmitting}>
                Next
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
};
